import type { QueueItem, PlayerRepeatMode } from "./queue.types";

/**
 * Songs with status "external" (unmatched imported-playlist songs) have no
 * playable audio. Keep this as the single place the queue decides
 * playability so callers don't each reimplement the same status check.
 */
export function isPlayableSong(song: { status: string }): boolean {
  return song.status !== "external";
}

export function getNextIndex(
  currentIndex: number,
  itemsCount: number,
  repeatMode: PlayerRepeatMode,
): number | null {
  if (itemsCount === 0) return null;

  if (repeatMode === "track") return currentIndex;

  const nextIndex = currentIndex + 1;
  if (nextIndex < itemsCount) {
    return nextIndex;
  }

  if (repeatMode === "queue") {
    return 0;
  }

  return null;
}

export function getPrevIndex(
  currentIndex: number,
  itemsCount: number,
  repeatMode: PlayerRepeatMode,
): number | null {
  if (itemsCount === 0) return null;

  if (repeatMode === "track") return currentIndex;

  const prevIndex = currentIndex - 1;
  if (prevIndex >= 0) {
    return prevIndex;
  }

  if (repeatMode === "queue") {
    return itemsCount - 1;
  }

  return 0; // Stick at 0 if no repeat
}

// Continuous-listening scope (operator spec 2026-08-02): shuffle and repeat
// operate on "everything the listener committed to" — the original collection
// they chose, plus every recommendation actually reached. The boundary is a
// high-water mark: max(last collection row, furthest position played). It
// only moves forward — wrapping back into the collection under repeat does
// NOT shrink the loop. Rows beyond the boundary (prefetched, not-yet-reached
// recommendations) trail visibly in the queue, untouched by shuffle and
// outside the repeat loop, until playback or an explicit jump reaches them.
export function playlistScopeEnd(queue: {
  items: { source?: string }[];
  currentIndex: number;
  highWaterIndex?: number;
}): number {
  let lastCollection = -1;
  for (let i = queue.items.length - 1; i >= 0; i--) {
    if (queue.items[i].source !== "playlist-recommendation") {
      lastCollection = i;
      break;
    }
  }
  const highWater = Math.max(queue.highWaterIndex ?? -1, queue.currentIndex);
  return Math.max(lastCollection, highWater);
}

export function shuffleList<T>(list: T[], excludeIndex?: number): T[] {
  const result = [...list];
  const excludeItem = excludeIndex !== undefined ? result.splice(excludeIndex, 1)[0] : null;

  // Fisher-Yates
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }

  if (excludeItem) {
    result.unshift(excludeItem);
  }

  return result;
}

export function shouldFetchMoreForYouRecommendations(
  currentIndex: number,
  itemsCount: number,
): boolean {
  if (itemsCount === 0) return false;
  const threshold = 3;
  return itemsCount - currentIndex <= threshold;
}

export function shouldFetchMorePlaylistRecommendations(
  currentIndex: number,
  itemsCount: number,
): boolean {
  if (itemsCount === 0) return false;

  const remaining = itemsCount - 1 - currentIndex;
  // Trigger when 35% or less remains, or if very few songs left
  return remaining / itemsCount <= 0.35 || (itemsCount < 10 && remaining <= 3);
}

export function createQueueItem(metadata: any): QueueItem {
  return {
    qid: `${metadata.id}-${Math.random().toString(36).substring(2, 11)}`,
    metadata,
  };
}

/**
 * Compute the repeat-all trim boundary.
 * When repeat all is enabled, we keep the original collection songs
 * (or at minimum everything up to and including the current song),
 * and drop unplayed recommendations beyond that.
 *
 * Returns the number of items to keep, or null if no trimming needed.
 */
export function getRepeatAllBoundary(
  currentIndex: number,
  queueLength: number,
  originalCollectionLength: number | null,
): number | null {
  const boundary = originalCollectionLength !== null
    ? Math.max(originalCollectionLength, currentIndex + 1)
    : currentIndex + 1;

  if (boundary < queueLength) {
    return boundary;
  }
  return null;
}

/**
 * Determine whether the queue should be trimmed when repeat all is toggled on.
 *
 * Returns the trim boundary (number of items to keep), or null if no trim should happen.
 *
 * Skip cases:
 * - Shuffle is ON (positional trimming is meaningless in shuffled order)
 * - Playlist with cache miss (can't determine original length, trimming risks cutting originals)
 */
export function shouldTrimOnRepeatAll(opts: {
  queueType: "playlist" | "foryou";
  currentIndex: number;
  queueLength: number;
  isShuffleOn: boolean;
  originalCollectionLength: number | undefined;
}): number | null {
  const { queueType, currentIndex, queueLength, isShuffleOn, originalCollectionLength } = opts;

  // Shuffle makes positional trimming meaningless
  if (queueType === "playlist" && isShuffleOn) return null;

  // Playlist cache miss — can't determine boundary safely
  if (queueType === "playlist" && originalCollectionLength === undefined) return null;

  return getRepeatAllBoundary(
    currentIndex,
    queueLength,
    queueType === "playlist" ? (originalCollectionLength ?? null) : null,
  );
}
